import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FeedCardAdmin } from '../../../components';
import {
  COLLAPSE_STORAGE_KEY,
  COPY_CUT_COOKIE_KEY,
  CollapseMode,
  CopyCutAction,
  FeedCardCollapsedLoader,
  FeedCardDrag,
  FeedCardLoader,
  STORY_TAB_ITEMS_ADMIN_COOKIE_KEY,
  SortableList,
  useAppDispatch,
  useAppSelector,
  useFeedCardReorder,
  useLastDate,
} from '../../../shared';
import { getStoryFeed, getStoryFeedLoadMore, updateStoryFeedCardPosition } from '../../../slices';
import { NoContent } from '../../Error';

import classes from './StoryFeedAdmin.module.scss';

interface StoryFeedAdminProps {
  storyId: number;
}

export const StoryFeedAdmin: FunctionComponent<StoryFeedAdminProps> = memo(({ storyId }) => {
  const dispatch = useAppDispatch();

  const { getDateRange } = useLastDate();

  const [cookies] = useCookies([COPY_CUT_COOKIE_KEY, STORY_TAB_ITEMS_ADMIN_COOKIE_KEY]);

  const {
    storyFeed: cards,
    page,
    hasNextPage,
    isFetched,
  } = useAppSelector(({ storyFeed }) => storyFeed);

  const reorderHandler = useFeedCardReorder({
    cards,
    updatePosition: updateStoryFeedCardPosition,
  });

  const { cardId: copyCutCardId = 0, action: copyCutAction = '' } = {
    ...cookies[COPY_CUT_COOKIE_KEY],
  };

  const { requestFilter = { status: [] } } = { ...cookies[STORY_TAB_ITEMS_ADMIN_COOKIE_KEY] };

  const { dateRange, ...requestFilterRest } = requestFilter;

  const filter = useMemo(() => {
    return { ...requestFilterRest, ...getDateRange(dateRange) };
  }, [dateRange, getDateRange, requestFilterRest]);

  const [collapseMode, setCollapseMode] = useState(
    localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD
  );

  const isCollapsed = useMemo(() => collapseMode === CollapseMode.COLLAPSED, [collapseMode]);

  useEffect(() => {
    window.addEventListener('storage', () => {
      setCollapseMode(localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD);
    });
  }, []);

  useEffect(() => {
    dispatch(getStoryFeed({ storyId, filter }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, storyId]);

  const loadMore = useCallback(() => {
    dispatch(getStoryFeedLoadMore({ storyId, page: page + 1, filter }));
  }, [dispatch, filter, page, storyId]);

  const hasCards = useMemo(() => Boolean(cards.length), [cards.length]);

  const loader = useMemo(
    () => (isCollapsed ? <FeedCardCollapsedLoader /> : <FeedCardLoader />),
    [isCollapsed]
  );

  const content = useMemo(() => {
    if (!hasCards) {
      return <NoContent />;
    }

    const cutCardId = copyCutAction === CopyCutAction.CUT ? copyCutCardId : null;

    const sortableCardIds = cards
      .filter(({ id, permissions }) => permissions.allowToUpdatePosition && id !== cutCardId)
      .map(({ id }) => id);

    return (
      <InfiniteScroll
        next={loadMore}
        hasMore={hasNextPage}
        loader={loader}
        dataLength={cards.length}
        className={classes['story-feed']}
      >
        <div className={classes['story-feed__cards']}>
          <SortableList items={sortableCardIds} onReorder={reorderHandler}>
            {cards.map((card) => {
              const { id: storyCardId } = card;

              if (!sortableCardIds.includes(storyCardId)) {
                return <FeedCardAdmin key={storyCardId} card={card} />;
              }

              return (
                <FeedCardDrag key={storyCardId} storyCardId={storyCardId}>
                  <FeedCardAdmin card={card} />
                </FeedCardDrag>
              );
            })}
          </SortableList>
        </div>
      </InfiniteScroll>
    );
  }, [
    cards,
    copyCutAction,
    copyCutCardId,
    hasCards,
    hasNextPage,
    loadMore,
    loader,
    reorderHandler,
  ]);

  if (!isFetched) {
    return loader;
  }

  return content;
});
